import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/AuthContext';

/**
 * Helper function to create a field object
 * @function
 * @param {string} label - The label key for translation
 * @param {string|number|ReactNode} value - The value to display
 * @param {number} span - The span value
 * @param {boolean} hidden - Whether the field is hidden
 * @returns {Object} Field object
 */
const createField = (label, value, span = 1, hidden = false) => ({
  label,
  span,
  content: value || '-',
  ...(hidden && { hidden })
});

/**
 * Creates a yes/no field
 * @function
 * @param {string} label - The label key
 * @param {boolean} value - The boolean value
 * @param {Function} t - Translation function
 * @param {number} span - The span value
 * @param {boolean} hidden - Whether the field is hidden
 * @returns {Object} Field object
 */
const createYesNoField = (label, value, t, span = 1, hidden = false) =>
  createField(
    label,
    value ? t('owners.show.yes') : t('owners.show.no'),
    span,
    hidden
  );

/**
 * Formats phone number for display
 * @function
 * @param {Object} phoneNumber - Phone number object
 * @returns {string} Formatted phone number
 */
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber?.number) return '-';
  const countryCode = phoneNumber?.country_code ?? '';
  const formattedNumber = phoneNumber.number.slice(1).replace(/(.{2})/g, '$1 ');
  return `${countryCode} ${formattedNumber}`;
};

/**
 * Gets insurance company fields for admin/owner roles
 * @function
 * @param {Object} insuranceCompany - Insurance company data
 * @param {string} policeNumber - Police number
 * @returns {Array} Array of field objects
 */
const getInsuranceCompanyFields = (insuranceCompany, policeNumber) => [
  createField(
    'petsittingaddress.form.insurance_company.consulting_room_name',
    insuranceCompany?.consulting_room_name,
    1
  ),
  createField(
    'petsittingaddress.form.insurance_company.address',
    insuranceCompany?.address,
    2
  ),
  createField(
    'petsittingaddress.form.insurance_company.full_phone_number',
    formatPhoneNumber(insuranceCompany?.phone_number),
    1
  ),
  createField('petsittingaddress.form.police_number', policeNumber, 2)
];

/**
 * Generates an array of objects containing formatted address and dwelling info for a pet sitting location.
 *
 * @function
 * @param {Object} data - The data object containing information about the pet sitting address and dwelling characteristics.
 * @param {Object} data.address - The address object with details about the pet sitting location's address.
 * @param {string} data.address.number - The house number.
 * @param {string} data.address.street - The street name.
 * @param {string} data.address.city - The city name.
 * @param {string} data.address.postal_code - The postal code.
 * @param {string} data.address.state - The state or department name.
 * @param {string} data.address.country - The country name.
 * @param {string} data.type_of_dwelling - The type of dwelling.
 * @param {number} data.living_space - The amount of living space (in an appropriate unit).
 * @param {number} data.available_pieces_number - The number of available pieces or rooms.
 * @param {Function} t - The translation function to be used for translating living space label.
 *
 * @returns {Array<Object>} - Returns an array of objects with `label`, `span`, and `content` properties, ready to be used in a form.
 *
 */
export const generalPetsittingAddressInfo = (data) => {
  const { t } = useTranslation();

  const { address, type_of_dwelling, living_space, available_pieces_number } =
    data || {};
  const { number, street, city, postal_code, state, country } = address || {};

  return [
    {
      label: 'petsittingaddress.form.address.street',
      span: 1,
      content: number || street ? `${number || ''} ${street || ''}` : '-'
    },
    {
      label: 'petsittingaddress.form.address.postal_code',
      span: 1,
      content: postal_code || '-'
    },
    {
      label: 'petsittingaddress.form.address.city',
      span: 1,
      content: city || '-'
    },
    {
      label: 'petsittingaddress.form.department',
      span: 1,
      content: state || '-'
    },
    {
      label: 'petsittingaddress.form.address.country',
      span: 1,
      content: country || '-'
    },
    {
      label: 'petsittingaddress.form.type_of_dwelling',
      span: 1,
      content:
        (type_of_dwelling && t(`petsittingaddress.tags.${type_of_dwelling}`)) ||
        '-'
    },
    {
      label: 'petsittingaddress.form.living_space',
      span: 1,
      content:
        (living_space &&
          t('petsittingaddress.form.living_space_label', {
            number: living_space
          })) ||
        '-'
    },
    {
      label: 'petsittingaddress.form.available_pieces_number',
      span: 1,
      content: available_pieces_number || '-'
    }
  ];
};

/**
 * Generates an array of objects containing detailed address and related info for a pet sitting location.
 *
 * @function
 * @param {Object} data - The data object containing comprehensive information about the pet sitting address and surrounding characteristics.
 * @param {Object} data.address - The address object containing details about the pet sitting location's address.
 * @param {string} data.address.number - The house number.
 * @param {string} data.address.street - The street name.
 * @param {string} data.address.city - The city name.
 * @param {string} data.address.postal_code - The postal code.
 * @param {string} data.address.state - The state or department name.
 * @param {string} data.address.country - The country name.
 * @param {string} data.type_of_dwelling - The type of dwelling.
 * @param {Object} data.insurance_company - Information regarding the insurance company associated with the pet sitting location.
 * @param {string} data.insurance_company.consulting_room_name - The name of the consulting room of the insurance company.
 * @param {string} data.insurance_company.address - The address of the insurance company.
 * @param {Object} data.insurance_company.phone_number - The phone number details of the insurance company.
 * @param {string} data.insurance_company.phone_number.number - The full phone number of the insurance company.
 * @param {string} data.police_number - The police number associated with the location.
 * @param {Object} data.criterion - Additional criterion data (not used in the current function scope, but included for future extensions).
 * @param {number} data.living_space - The living space details (not used in the current function scope, but included for future extensions).
 * @param {number} data.available_pieces_number - Details of available pieces or rooms (not used in the current function scope, but included for future extensions).
 * @param {boolean} data.is_air_conditioned_house - Information if the house has air conditioning (not used in the current function scope, but included for future extensions).
 * @param {boolean} data.small_business_nearby - Information if there are small businesses nearby (not used in the current function scope, but included for future extensions).
 * @param {boolean} data.super_market_nearby - Information if there is a supermarket nearby (not used in the current function scope, but included for future extensions).
 * @param {Array<string>} data.means_of_transports_nearby - Details of nearby means of transports (not used in the current function scope, but included for future extensions).
 * @param {string} data.station_distance - Details on the distance to the nearest station (not used in the current function scope, but included for future extensions).
 * @param {string} data.nearby_city - Details on the nearby city (not used in the current function scope, but included for future extensions).
 * @param {string} data.neighbor_distance - Details on the distance to the nearest neighbor (not used in the current function scope, but included for future extensions).
 * @param {Array<string>} data.nearby_site - Details on nearby sites (not used in the current function scope, but included for future extensions).
 * @param {boolean} data.code_lock - Information on if there is a code lock present (not used in the current function scope, but included for future extensions).
 * @param {number} data.floor_number - Details on the floor number of the dwelling (not used in the current function scope, but included for future extensions).
 * @param {boolean} data.elevator - Information if the dwelling has an elevator (not used in the current function scope, but included for future extensions).
 * @param {string} data.type_of_storage_for_pensioner - Details on the type of storage available for pensioners (not used in the current function scope, but included for future extensions).
 * @param {string} title - The title for the pet sitting address details (not used in the current function scope, but included for future extensions).
 * @param {Function} t - The translation function to be used for translating living space label (not used in the current function scope, but included for future extensions).
 *
 * @returns {Array<Object>} - Returns an array of objects with `label`, `span`, and `content` properties, ready to be used in a form.
 *
 * @example
 */
export const detailPetsittingAddressInfo = (data, title, t) => {
  const { user } = useAuthContext();

  const {
    address,
    type_of_dwelling,
    insurance_company,
    police_number,
    type_of_environment,
    living_space,
    available_pieces_number,
    is_air_conditioned_house,
    small_business_nearby,
    super_market_nearby,
    means_of_transports_nearby,
    station_distance,
    nearby_city,
    neighbor_distance,
    nearby_site,
    code_lock,
    floor_number,
    elevator,
    type_of_storage_for_pensioner
  } = data;

  const isAdminOrOwner =
    user?.role === 'admins:ADMIN' || user?.role === 'users:OWNER';
  const isHouse = type_of_dwelling === 'HOUSE';

  const details = [
    createField(
      'petsittingaddress.form.type_of_dwelling',
      type_of_dwelling && t(`petsittingaddress.tags.${type_of_dwelling}`),
      1
    ),
    createField(
      'petsittingaddress.form.address.street',
      address && `${address?.number || ''} ${address?.street || ''}`,
      2
    ),
    createField('petsittingaddress.form.address.city', address?.city, 3),
    createField(
      'petsittingaddress.form.address.postal_code',
      address?.postal_code,
      1
    ),
    createField('petsittingaddress.form.department', address?.state, 2),
    createField('petsittingaddress.form.address.country', address?.country, 3),
    ...(isAdminOrOwner
      ? getInsuranceCompanyFields(insurance_company, police_number)
      : [])
  ];

  const criterionContent =
    type_of_environment?.length &&
    type_of_environment
      .map((env) => t(`petsittingaddress.form.environment.${env}`))
      .join(', ');

  const generalInformation = [
    createField('petsittingaddress.form.criterion', criterionContent, 1),
    createField(
      'petsittingaddress.form.living_space',
      living_space &&
        t('petsittingaddress.form.living_space_label', {
          number: living_space
        }),
      2
    ),
    createField(
      'petsittingaddress.form.available_pieces_number',
      available_pieces_number,
      1
    ),
    createField('petsittingaddress.form.code_lock', code_lock, 1, isHouse),
    createField(
      'petsittingaddress.form.floor_number',
      floor_number,
      2,
      isHouse
    ),
    createYesNoField(
      'petsittingaddress.form.elevator',
      elevator,
      t,
      3,
      isHouse
    ),
    createYesNoField(
      'petsittingaddress.form.is_air_conditioned_house',
      is_air_conditioned_house,
      t,
      2
    ),
    createField(
      'petsittingaddress.form.small_business_nearby.label',
      small_business_nearby?.name_and_distance,
      1
    ),
    createField(
      'petsittingaddress.form.super_market_nearby.label',
      super_market_nearby?.name_and_distance,
      2
    ),
    createField(
      'petsittingaddress.form.means_of_transports_nearby',
      means_of_transports_nearby,
      1
    ),
    createField('petsittingaddress.form.station_distance', station_distance, 2),
    createField('petsittingaddress.form.nearby_city', nearby_city, 1),
    createField(
      'petsittingaddress.form.neighbor_distance',
      neighbor_distance,
      2
    ),
    createField('petsittingaddress.form.nearby_site', nearby_site, 1)
  ];

  const roomAndLaundryData =
    type_of_storage_for_pensioner?.room_and_laundry || {};

  const roomAndLaundry = [
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.cleaner_during_supervision',
      roomAndLaundryData.cleaner_during_supervision,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.room_in_ground_floor',
      roomAndLaundryData.room_in_ground_floor,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.air_conditioned_room',
      roomAndLaundryData.air_conditioned_room,
      t,
      1
    ),
    createField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.rooms_number_for_pensioner',
      roomAndLaundryData.rooms_number_for_pensioner,
      2
    ),
    createField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.bed',
      roomAndLaundryData.bed,
      1
    ),
    createField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.tidying_type',
      roomAndLaundryData.tidying_type,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.sheet',
      roomAndLaundryData.sheet,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.household_linen',
      roomAndLaundryData.household_linen,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.washer',
      roomAndLaundryData.washer,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.dryer',
      roomAndLaundryData.dryer,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.room_and_laundry.iron',
      roomAndLaundryData.iron,
      t,
      1
    )
  ];

  const bathroomData = type_of_storage_for_pensioner?.bathroom || {};

  const bathroomInformation = [
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.bathroom.shower',
      bathroomData.shower,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.bathroom.bathroom_towel',
      bathroomData.bathroom_towel,
      t,
      2
    )
  ];

  const kitchenData = type_of_storage_for_pensioner?.kitchen || {};

  const kitchenInformation = [
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.kitchen.hotplate',
      kitchenData.hotplate,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.kitchen.oven',
      kitchenData.oven,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.kitchen.microwave',
      kitchenData.microwave,
      t,
      3
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.kitchen.electric_coffee_maker',
      kitchenData.electric_coffee_maker,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.kitchen.toaster',
      kitchenData.toaster,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.kitchen.dishwasher',
      kitchenData.dishwasher,
      t,
      3
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.kitchen.freezer',
      kitchenData.freezer,
      t,
      2
    )
  ];

  const outsideData = type_of_storage_for_pensioner?.outside || {};
  const surfaceContent =
    outsideData.surface &&
    t(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.surface_label',
      {
        number: outsideData.surface
      }
    );

  const outsideInformation = [
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.garden',
      outsideData.garden,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.closed',
      outsideData.closed,
      t,
      2
    ),
    createField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.surface',
      surfaceContent,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.gardener_during_supervision',
      outsideData.gardener_during_supervision,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.automatic_spraying',
      outsideData.automatic_spraying,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.external_plant',
      outsideData.external_plant,
      t,
      2
    ),
    createField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.external_time_to_give',
      outsideData.external_time_to_give,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.interior_plant',
      outsideData.interior_plant,
      t,
      2
    ),
    createField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.interior_time_to_give',
      outsideData.interior_time_to_give,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.swimming_pool',
      outsideData.swimming_pool,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.jacuzzi',
      outsideData.jacuzzi,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.garden_furniture',
      outsideData.garden_furniture,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.barbecue',
      outsideData.barbecue,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.balcony',
      outsideData.balcony,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.outside.patio',
      outsideData.patio,
      t,
      1
    )
  ];

  const formatParkingData = () => {
    if (type_of_storage_for_pensioner?.parking_and_installation?.parking) {
      const selectedOptions = Object.keys(
        type_of_storage_for_pensioner?.parking_and_installation?.parking
      ).filter(
        (key) =>
          type_of_storage_for_pensioner?.parking_and_installation?.parking[key]
      );

      const translatedOptions = selectedOptions.map((key) =>
        t(
          `petsittingaddress.form.type_of_storage_for_pensioner.parking_and_installation.parking.${key}`
        )
      );

      return translatedOptions.join(', ');
    }
    return '-';
  };

  const installationData =
    type_of_storage_for_pensioner?.parking_and_installation?.installation || {};

  const parkingAndInstallation = [
    createField(
      'petsittingaddress.form.type_of_storage_for_pensioner.parking_and_installation.parking.label',
      formatParkingData(),
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.parking_and_installation.installation.alarm',
      installationData.alarm,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.parking_and_installation.installation.telesurveillance',
      installationData.telesurveillance,
      t,
      1
    )
  ];
  const equipmentData = type_of_storage_for_pensioner?.equipment || {};

  const equipmentInformation = [
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.equipment.television',
      equipmentData.television,
      t,
      1
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.equipment.cable',
      equipmentData.cable,
      t,
      2
    ),
    createYesNoField(
      'petsittingaddress.form.type_of_storage_for_pensioner.equipment.internet',
      equipmentData.internet,
      t,
      3
    )
  ];

  const addressData = {
    details,
    general_informations: generalInformation,
    room_and_laundry: roomAndLaundry,
    bathroom: bathroomInformation,
    kitchen: kitchenInformation,
    outside: outsideInformation,
    parking_and_installation: parkingAndInstallation,
    equipment: equipmentInformation
  };
  return addressData[title];
};
